"use client";

import {
  UserCheckoutBody,
  UserPlanOption,
  UserPlanVariant,
  userPlanVariantData,
  userPlanVariantOptions,
  UserSubscriptionStatus,
} from "@/lib/data/accounts/plans";
import { useClerk } from "@clerk/nextjs";
import { useMutation } from "@tanstack/react-query";
import { Check, CalendarRange, Crown, Timer } from "lucide-react";
import { useFormatter, useLocale, useTranslations } from "next-intl";
import React, { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ProPlanDialog } from "./ProPlanDialog";
import { CurrentPlanSummary } from "./CurrentPlanSummary";

const PLAN_ICONS: Record<
  UserPlanVariant,
  React.ComponentType<{ className?: string }>
> = {
  PRO_3_DAYS: Timer,
  PRO_1_WEEK: CalendarRange,
  PRO_1_MONTH: Crown,
};

type Props = {
  currentPlan: UserPlanOption | null;
  currentVariant: UserPlanVariant | null;
  isLoggedIn: boolean;
  subscriptionStatus: UserSubscriptionStatus;
  currentPeriodEnd: Date | null;
  cancelAtPeriodEnd: boolean;
};

const featureKeys = [
  "no-ads",
  "powerful-search",
  "listing-views",
  "search-alerts",
  "rental-organizer",
  "smart-messages",
] as const;

const ProPlansView: React.FC<Props> = ({
  currentPlan,
  currentVariant,
  isLoggedIn,
  subscriptionStatus,
  currentPeriodEnd,
  cancelAtPeriodEnd,
}) => {
  const t = useTranslations("pro.pricing");
  const locale = useLocale();
  const formatter = useFormatter();
  const clerk = useClerk();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupType, setPopupType] = useState<
    "success" | "canceled" | "error" | null
  >(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);

    if (query.get("success")) {
      setIsPopupOpen(true);
      setPopupType("success");
      setSessionId(query.get("session_id"));
    } else if (query.get("canceled")) {
      setIsPopupOpen(true);
      setPopupType("canceled");
      setSessionId(query.get("session_id"));
    } else if (query.get("error")) {
      setIsPopupOpen(true);
      setPopupType("error");
    }
  }, []);

  const { mutate } = useMutation({
    mutationFn: async (variant: UserCheckoutBody) => {
      console.log("Starting checkout for variant:", variant);
      const response = await fetch("/api/service/billing/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(variant),
      });

      if (!response.ok) {
        setIsPopupOpen(true);
        setPopupType("error");
        throw new Error("Checkout error");
      }

      return (await response.json()) as {
        type: "checkout" | "portal";
        url: string;
      };
    },
    onSuccess: (data) => {
      if (!data?.url) return;
      window.location.assign(data.url);
    },
  });

  const onVariantButtonClick = (variant: UserPlanVariant) => {
    if (!isLoggedIn) {
      // IDEA: highlight the plan after successful sign in
      clerk.openSignIn({
        forceRedirectUrl: window.location.href + `#plans`,
      });
      return;
    }

    mutate({
      baseUrl: window.location.origin + window.location.pathname,
      locale: locale,
      variant: variant,
    });
  };
  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-5xl scroll-mt-20" id="plans">
        <div className="flex flex-col items-center gap-3 text-center">
          <Badge variant="secondary" className="w-fit">
            {t("badge")}
          </Badge>
          <h2 className="text-balance text-2xl font-semibold tracking-tight sm:text-3xl">
            {t("title")}
          </h2>
          <p className="max-w-2xl text-balance text-sm text-muted-foreground sm:text-base">
            {t("description")}
          </p>
        </div>
        <div className="grid items-stretch gap-4 pt-8 md:grid-cols-3 lg:gap-6">
          {userPlanVariantOptions.map((variant) => {
            const data = userPlanVariantData[variant];
            const isFeatured = variant === "PRO_1_MONTH";
            const PlanIcon = PLAN_ICONS[variant];

            const isCurrent =
              currentPlan === "PRO" && currentVariant === variant;

            let buttonLabel = t("button.upgrade-plan");

            if (!isLoggedIn) {
              buttonLabel = t("button.continue-sign-in");
            } else if (currentPlan === "FREE") {
              buttonLabel = t("button.upgrade-pro");
            } else if (isCurrent) {
              buttonLabel = t("button.current-plan");
            } else if (currentPlan === "PRO" && !isCurrent) {
              buttonLabel = t("button.switch-plan");
            }

            const isDisabled = isCurrent;

            return (
              <div
                key={variant}
                className={twMerge(
                  "relative flex flex-col gap-4 overflow-hidden rounded-2xl border p-6 shadow-sm",
                  isFeatured
                    ? "border-transparent bg-sidebar-primary text-white ring-2 ring-secondary"
                    : "border-border bg-background",
                  isCurrent && !isFeatured && "border-primary",
                )}
              >
                {isFeatured && (
                  <div className="pointer-events-none absolute right-0 top-0 h-0 w-0 border-l-[160px] border-t-[160px] border-l-transparent border-t-secondary/15" />
                )}

                <div className="relative flex flex-row items-center justify-between gap-2">
                  <div className="flex size-10 items-center justify-center rounded-xl bg-secondary text-secondary-foreground">
                    <PlanIcon className="size-5" />
                  </div>
                  {isFeatured && (
                    <Badge variant="secondary">{t("most-popular")}</Badge>
                  )}
                </div>

                <div className="relative space-y-1">
                  <h3 className="text-sm font-semibold sm:text-base">
                    {t(`plans.${variant}.title`)}
                  </h3>
                  <div className="flex flex-row items-baseline gap-1.5">
                    <span className="text-3xl font-bold">
                      {formatter.number(data.priceCents / 100, {
                        style: "currency",
                        currency: "EUR",
                      })}
                    </span>
                    <span
                      className={twMerge(
                        "text-xs",
                        isFeatured ? "text-white/60" : "text-muted-foreground",
                      )}
                    >
                      {t(`plans.${variant}.interval`)}
                    </span>
                  </div>
                </div>
                <p
                  className={twMerge(
                    "relative text-sm leading-relaxed md:min-h-14",
                    isFeatured ? "text-white/75" : "text-muted-foreground",
                  )}
                >
                  {t(`plans.${variant}.description`)}
                </p>

                <div className="relative space-y-2">
                  {featureKeys.map((key) => (
                    <div key={key} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 size-4 min-w-4 rounded-full bg-secondary p-0.5 text-secondary-foreground" />
                      <span
                        className={twMerge(
                          isFeatured ? "text-white/90" : "text-foreground",
                        )}
                      >
                        {t(`features.${key}`)}
                      </span>
                    </div>
                  ))}
                </div>

                {isCurrent && (
                  <Badge
                    variant="outline"
                    className={twMerge(
                      "w-fit self-center",
                      isFeatured && "border-white/30 text-white",
                    )}
                  >
                    {t("your-current-plan")}
                  </Badge>
                )}

                <Button
                  type="button"
                  disabled={isDisabled}
                  variant={isFeatured ? "secondary" : "default"}
                  className="relative mt-auto h-10 w-full font-semibold"
                  onClick={() => onVariantButtonClick(variant)}
                >
                  {buttonLabel}
                </Button>
              </div>
            );
          })}
        </div>
        <p className="mt-6 text-center text-xs text-muted-foreground sm:text-sm">
          {t("renewal-notice")}
        </p>
        <CurrentPlanSummary
          currentPlan={currentPlan}
          currentVariant={currentVariant}
          subscriptionStatus={subscriptionStatus}
          currentPeriodEnd={currentPeriodEnd}
          cancelAtPeriodEnd={cancelAtPeriodEnd}
          onManageBilling={() => {
            window.location.href = "/api/service/billing/portal";
          }}
        />
        <ProPlanDialog
          open={isPopupOpen}
          type={popupType}
          sessionId={sessionId}
          onOpenChange={setIsPopupOpen}
          onRetry={() => {
            // optional: you can clear query params here or just let user click again
            // for now this just closes and lets them click a plan button again
          }}
          onGoToDashboard={() => {
            window.location.href = "/dashboard/tenant/listing";
          }}
        />
      </div>
    </div>
  );
};

export default ProPlansView;
